import { useEffect, useState } from 'react'
import { fetchPendingDocuments } from 'api/dashboardDocuments'
import { fetchPendingRecommendations } from 'api/dashboardRecommendations'
import {
	syncStudentDocumentsSeen,
	syncStudentRecommendationsSeen,
} from 'shared/utils/counselorSeenSync'

const EMPTY = {
	docFileIds: new Set(),
	lorFileIds: new Set(),
	docCount: 0,
	lorCount: 0,
}

const buildIdSet = list =>
	new Set(
		(Array.isArray(list) ? list : [])
			.map(item => String(item?.id || '').trim())
			.filter(Boolean),
	)

export const useStudentNewArtifacts = (studentId, isActive) => {
	const [snapshot, setSnapshot] = useState(EMPTY)

	useEffect(() => {
		const sid = String(studentId || '').trim()
		if (!sid || !isActive) {
			setSnapshot(EMPTY)
			return undefined
		}

		let alive = true
		const run = async () => {
			try {
				const [docsRes, recsRes] = await Promise.all([
					fetchPendingDocuments(),
					fetchPendingRecommendations(),
				])
				if (!alive) return

				const docItem = (docsRes?.data?.items || []).find(
					it => String(it?.studentId || '').trim() === sid,
				)
				const recItems = (recsRes?.data?.items || []).filter(
					it => String(it?.studentId || '').trim() === sid,
				)

				const docFileIds = buildIdSet(docItem?.newDocuments)
				const lorFileIds = buildIdSet(
					recItems.flatMap(it =>
						Array.isArray(it?.uploadedFiles) ? it.uploadedFiles : [],
					),
				)
				const docCount = Number(docItem?.newCount) || docFileIds.size
				const lorCount =
					recItems.reduce((acc, it) => acc + (Number(it?.newCount) || 0), 0) ||
					lorFileIds.size

				setSnapshot({ docFileIds, lorFileIds, docCount, lorCount })

				if (docItem) {
					syncStudentDocumentsSeen(sid, {
						anchorAt: docItem.lastUpdatedAt || undefined,
						fileCountForUi: docCount,
					})
				}
				if (recItems.length > 0) {
					syncStudentRecommendationsSeen(sid, {
						anchorAt: recItems[0]?.lastUpdatedAt || undefined,
					})
				}
			} catch {
				if (alive) setSnapshot(EMPTY)
			}
		}
		run()
		return () => {
			alive = false
		}
	}, [studentId, isActive])

	return snapshot
}

export default useStudentNewArtifacts
